import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { Subject, of } from 'rxjs';
import { filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import {
  IRaffle,
  IRaffleConditionRaffle,
  IRaffleRewardRaffle,
} from './raffle.interfaces';
import { RafflesService } from './raffles.service';
import { getRaffleError } from './raffle-error.util';
import { RaffleEditDialogComponent, IRaffleEditResult } from './raffle-edit-dialog.component';

@Component({
  selector: 'app-raffle-relations-dialog',
  templateUrl: './raffle-relations-dialog.component.html',
  styles: [
    'table { width: 100%; border-collapse: collapse; } td, th { text-align: left; padding: 12px; border-bottom: 1px solid #7774; } .error { color: #e85b5b; }',
  ],
})
export class RaffleRelationsDialogComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private editDialog?: MatDialogRef<RaffleEditDialogComponent>;
  conditions: IRaffleConditionRaffle[] = [];
  rewards: IRaffleRewardRaffle[] = [];
  weights: Record<number, number> = {};
  loading = false;
  busy = false;
  changed = false;
  error = '';
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: {
      userId: number;
      raffle: IRaffle;
      mode: 'conditions' | 'rewards';
      canEdit: boolean;
    },
    private readonly api: RafflesService,
    private readonly dialog: MatDialog,
    public readonly ref: MatDialogRef<RaffleRelationsDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.load();
  }
  load(): void {
    this.loading = true;
    this.error = '';
    if (this.data.mode === 'rewards') {
      this.api
        .getRewards(this.data.raffle)
        .pipe(
          finalize(() => (this.loading = false)),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: rows => (this.rewards = rows),
          error: error => (this.error = getRaffleError(error)),
        });
      return;
    }
    this.api
      .getConditions(this.data.raffle)
      .pipe(
        switchMap(rows => {
          this.conditions = rows;
          this.weights = {};
          return rows.length
            ? this.api.getUserConditions(
                this.data.userId,
                rows.map(row => row.id),
              )
            : of([]);
        }),
        finalize(() => (this.loading = false)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: rows => {
          for (const row of rows) {
            const id =
              typeof row.raffleConditionRaffle === 'number'
                ? row.raffleConditionRaffle
                : row.raffleConditionRaffle.id;
            this.weights[id] = (this.weights[id] ?? 0) + row.conditionsCount;
          }
        },
        error: error => (this.error = getRaffleError(error)),
      });
  }

  won(reward: IRaffleRewardRaffle): boolean {
    return (
      reward.winners?.some(winner => winner.userId === this.data.userId) ??
      false
    );
  }

  rewardName(row: IRaffleRewardRaffle): string {
    if (row.reward.item?.name) return row.reward.item.name;
    if (row.reward.type === 'wallet') return 'Баланс';
    if (row.reward.type === 'exp') return 'Опыт';
    return row.reward.type + ' #' + (row.reward.itemId ?? row.reward.id);
  }

  change(condition: IRaffleConditionRaffle, remove: boolean): void {
    if (
      !this.data.canEdit ||
      this.data.raffle.raffleFinish ||
      this.busy ||
      this.loading ||
      this.error
    )
      return;
    this.editDialog = this.dialog.open(RaffleEditDialogComponent, {
      width: '560px',
      data: {
        title:
          (remove ? 'Снять условие' : 'Засчитать / изменить вес') +
          ' #' +
          condition.id,
        description:
          'Пользователь ' +
          this.data.userId +
          ', раффл #' +
          this.data.raffle.id +
          '. ' +
          (remove
            ? 'Будут сняты все выполнения этого условия. При отсутствии остальных условий участие также удаляется.'
            : 'Указанное число заменяет суммарный вес всех выполнений этого условия. Билеты не списываются и не возвращаются.'),
        count: remove ? undefined : this.weights[condition.id] || 1,
      },
    });
    this.editDialog
      .afterClosed()
      .pipe(
        filter((value: IRaffleEditResult | undefined): value is IRaffleEditResult => !!value),
        switchMap(value => {
          this.busy = true;
          this.ref.disableClose = true;
          return (remove
            ? this.api.deleteCondition(
                this.data.userId,
                condition.id,
                value.reason,
              )
            : this.api.updateCondition(
                this.data.userId,
                condition.id,
                value.count,
                value.reason,
              )
          ).pipe(
            finalize(() => {
              this.busy = false;
              this.ref.disableClose = false;
            }),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.changed = true;
          this.load();
        },
        error: error => (this.error = getRaffleError(error)),
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.editDialog?.close();
  }
}
